// @flow

import React, {useState, useEffect, useRef} from "react";
import {Col, Row, Table, Form, Input, InputNumber, Switch, Button, Radio, Checkbox} from "antd";
import {useFormik, FieldArray, FormikProvider, getIn, validateYupSchema} from "formik";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";

import {arrayAlphabeticalSort} from "../../../utils/helpers";
import {cities} from "../../../constants/constants";
import {ErrorFocus} from "../../_defaultComponents/ErrorFocus/ErrorFocus";
import {
    CompanyContanier,
    CompanyButtonContainer,
    SubCompanyContainer,
    FilterContainer,
    SelectField,
    RadioContainer
} from "./optionsStyles";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {mergeOptionsValidation} from "./mergeOptionsValidation";
import {handleCheckBoxText} from "../../../utils/helpers";
import {CityFilter} from "../../_defaultComponents/FilterComponents";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";

const {Column} = Table;

export const MergeOptions = ({companyId}) => {
    const stores = useStore();
    const history = useHistory();
    const {t} = useTranslation(["mergeOptions", "courierTypes"]);

    const [currentPage, setCurrentPage] = useState(1);
    const [isCompanyFormChanged, setIsCompanyFormChanged] = useState(false);
    const [companyFormData, setCompanyFormData] = useState();
    const [city, setCity] = useState("all");
    const [upperGroup, setUpperGroup] = useState("");
    const [regionGroups, setRegionGroups] = useState([]);
    const [regionGroupsNames, setRegionGroupsNames] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [text, setText] = useState("");
    const [showSubmitButton, setShowSubmitButton] = useState({});
    const [isSubCompanySubmitting, setIsSubCompanySubmitting] = useState(false);
    const [companyRecall, setCompanyRecall] = useState(false);
    const [subCompanyRecall, setSubCompanyRecall] = useState(false);
    const [radioValue, setRadioValue] = useState("motorCourier");

    const companyRecallRef = useRef(companyRecall);
    companyRecallRef.current = companyRecall;
    const subCompanyRecallRef = useRef(subCompanyRecall);
    subCompanyRecallRef.current = subCompanyRecall;

    const [companyLoading, companyData, setCompanyData, setCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.companyStore.getCompany.bind(null, {
            companyId,
            withSubCompanies: false,
            vehicleType: radioValue
        }),
        dependencies: [companyRecall, radioValue]
    });

    const [upperGroupsLoading, upperGroups, setUpperGroups, setUpperGroupsLoading] = useFetch({
        fetchFn: stores.regionStore.getUpperGroupsByCompanyIdAndCity.bind(null, {
            companyId,
            city
        }),
        dependencies: [companyId, city],
        initialCall: false
    });

    const onChange = e => {
        setRadioValue(e.target.value);
    };

    const [subCompanyLoading, subCompanyData, setSubCompanyData, setSubCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn:
            companyData.data && companyData.data._id
                ? stores.companyStore.getSubCompanyList.bind(null, {
                      companyId: companyData.data._id,
                      limit: 10,
                      skip: (currentPage - 1) * 10,
                      activity: "enabled",
                      vehicleType: radioValue,
                      city: city,
                      searchText,
                      upperGroup: upperGroup,
                      regionGroups: regionGroups?.join(".")
                  })
                : () => [],
        dependencies: [
            companyData,
            currentPage,
            subCompanyRecall,
            city,
            searchText,
            radioValue,
            upperGroup,
            regionGroups
        ]
    });

    const formik = useFormik({
        initialValues: {
            options: {
                merge: {
                    isActive: companyData?.data?.options?.merge?.isActive,
                    isPoolMergeActive: companyData?.data?.options?.merge?.isPoolMergeActive,
                    clickToDoorTime: companyData?.data?.options?.merge?.clickToDoorTime || 0,
                    prepTime: companyData?.data?.options?.merge?.prepTime || 0,
                    prepTimeBufferPercent:
                        companyData?.data?.options?.merge?.prepTimeBufferPercent || 0,
                    handOverTime: companyData?.data?.options?.merge?.handOverTime || 0,
                    maxOrder: companyData?.data?.options?.merge?.maxOrder || 0,
                    applyMergeOptions: false,
                    mergeWithOtherTypes:
                        companyData?.data?.options?.merge?.mergeWithOtherTypes || false,
                    mergeWithOtherCompanies:
                        companyData?.data?.options?.merge?.mergeWithOtherCompanies || false,
                    maxPickup: companyData?.data?.options?.merge?.maxPickup || false,
                    regionRestriction: companyData?.data?.options?.merge?.regionRestriction || false
                },
                ...companyData?.data?.options
            },
            ...(companyData && companyData.data)
        },
        enableReinitialize: true,
        validateOnChange: true,
        validationSchema: mergeOptionsValidation,
        onSubmit: async values => {
            setCompanyLoading(true);

            let filters = {};
            filters.searchText = searchText;
            filters.city = city;
            filters.upperGroup = upperGroup;
            filters.regionGroups = regionGroups;
            if (Object.keys(filters).length > 0) {
                values.filters = filters;
            }

            await stores.companyStore.updateCompanyMergeOptions(values);
            if (values.applyMergeOptions) {
                setSubCompanyRecall(!subCompanyRecallRef.current);
            }
            setCompanyRecall(!companyRecallRef.current);
            setCompanyLoading(false);
        }
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue
    } = formik;

    useEffect(() => {
        if (values && !isCompanyFormChanged) {
            setCompanyFormData(values);
        } else if (values && JSON.stringify(companyFormData) !== JSON.stringify(values)) {
            setIsCompanyFormChanged(true);
        } else {
            setIsCompanyFormChanged(false);
        }
    }, [values]);

    const onSubCompanySubmit = async index => {
        setIsSubCompanySubmitting(true);
        let submitData = subCompanyData.rows[index]?.options?.merge;
        submitData.name = subCompanyData.rows[index]?.name;
        submitData._id = subCompanyData.rows[index]?._id;
        if (submitData.clickToDoorTime <= 0 || submitData.clickToDoorTime.length === 0) {
            stores.appStore.openErrorModal({
                title: `${t("modal.errorTitle", {ns: "common"})}!`,
                content: `${submitData.name}, ${t("fields.mergeOptions.clickToDoorTime.label", {
                    ns: "common"
                })} (${t("fields.mergeOptions.clickToDoorTime.short", {
                    ns: "common"
                })}) ${t("validation.greaterThanZero", {
                    ns: "common"
                })}`,
                onOk: () => {}
            });
        } else if (submitData.prepTime <= 0 || submitData.prepTime.length === 0) {
            stores.appStore.openErrorModal({
                title: `${t("modal.errorTitle", {ns: "common"})}!`,
                content: `${submitData.name}, ${t("fields.mergeOptions.prepTime.label", {
                    ns: "common"
                })} (${t("fields.mergeOptions.prepTime.short", {ns: "common"})}) ${t(
                    "validation.greaterThanZero",
                    {
                        ns: "common"
                    }
                )}`,
                onOk: () => {}
            });
        } else if (
            submitData.prepTimeBufferPercent <= 0 ||
            submitData.prepTimeBufferPercent.length === 0
        ) {
            stores.appStore.openErrorModal({
                title: `${t("modal.errorTitle", {ns: "common"})}!`,
                content: `${submitData.name}, ${t(
                    "fields.mergeOptions.prepTimeBufferPercent.label",
                    {ns: "common"}
                )} (${t("fields.mergeOptions.prepTimeBufferPercent.short", {ns: "common"})}) ${t(
                    "validation.greaterThanZero",
                    {
                        ns: "common"
                    }
                )}`,
                onOk: () => {}
            });
        } else if (submitData.handOverTime <= 0 || submitData.handOverTime.length === 0) {
            stores.appStore.openErrorModal({
                title: `${t("modal.errorTitle", {ns: "common"})}!`,
                content: `${submitData.name}, ${t("fields.mergeOptions.handOverTime.label", {
                    ns: "common"
                })} (${t("fields.mergeOptions.handOverTime.short", {ns: "common"})}) ${t(
                    "validation.greaterThanZero",
                    {
                        ns: "common"
                    }
                )}`,
                onOk: () => {}
            });
        } else if (submitData.maxOrder <= 0 || submitData.maxOrder.length === 0) {
            stores.appStore.openErrorModal({
                title: `${t("modal.errorTitle", {ns: "common"})}!`,
                content: `${submitData.name}, ${t("fields.mergeOptions.mergeMaxOrder.label", {
                    ns: "common"
                })} (${t("fields.mergeOptions.mergeMaxOrder.short", {
                    ns: "common"
                })}) ${t("validation.greaterThanZero", {ns: "common"})}`,
                onOk: () => {}
            });
        } else if (submitData.maxPickup <= 0 || submitData.maxPickup.length === 0) {
            stores.appStore.openErrorModal({
                title: `${t("modal.errorTitle", {ns: "common"})}!`,
                content: `${submitData.name}, ${t("mergeOptions.mergePoint", {
                    ns: "mergeOptions"
                })} ${t("validation.greaterThanZero", {ns: "common"})}`,
                onOk: () => {}
            });
        } else {
            const params = {
                subCompanyId: submitData._id,
                options: {
                    isActive: submitData.isActive,
                    isPoolMergeActive: submitData.isPoolMergeActive,
                    clickToDoorTime: submitData.clickToDoorTime,
                    handOverTime: submitData.handOverTime,
                    maxOrder: submitData.maxOrder,
                    prepTime: submitData.prepTime,
                    prepTimeBufferPercent: submitData.prepTimeBufferPercent,
                    mergeWithOtherTypes: submitData.mergeWithOtherTypes,
                    mergeWithOtherCompanies: submitData.mergeWithOtherCompanies,
                    maxPickup: submitData.maxPickup,
                    regionRestriction: submitData.regionRestriction,
                    courierType: radioValue
                }
            };

            const result = await stores.companyStore.updateSubCompanyMergeOptions(params);
            if (result) {
                setSubCompanyRecall(!subCompanyRecallRef.current);
            }
        }
        setIsSubCompanySubmitting(false);
    };

    return (
        <div data-testid="mergeOptionsScreen">
            <h2 data-testid="companyName">{companyData?.data?.companyName}</h2>
            <RadioContainer>
                <Radio.Group
                    onChange={onChange}
                    value={radioValue}
                    data-testid="vehicleTypeRadioButton"
                >
                    <Radio
                        value={"motorCourier"}
                        data-testid="vehicleTypeRadioButton_option"
                        data-testvalue="motorCourier"
                    >
                        {t("longForm.motorCourier", {ns: "courierTypes"})}
                    </Radio>
                    <Radio
                        value={"carCourier"}
                        data-testid="vehicleTypeRadioButton_option"
                        data-testvalue="carCourier"
                    >
                        {t("longForm.carCourier", {ns: "courierTypes"})}
                    </Radio>
                </Radio.Group>
            </RadioContainer>
            <CompanyContanier>
                <Form
                    autoComplete="off"
                    data-testid="companyMergeSettingsForm"
                    onFinish={handleSubmit}
                    onChange={e => setIsCompanyFormChanged(true)}
                >
                    <Table
                        loading={companyLoading}
                        dataSource={[companyData.data]}
                        rowKey="_id"
                        pagination={false}
                        data-testid="companyMergeSettingsTable"
                        locale={{
                            emptyText: <EmptyMessage />
                        }}
                    >
                        <ErrorFocus formik={formik} />
                        <FormikProvider value={formik}>
                            <Column
                                title={`${t("fields.mergeOptions.orderMerge.label", {
                                    ns: "common"
                                })} (${t("fields.mergeOptions.orderMerge.short", {ns: "common"})})`}
                                dataIndex="isActive"
                                render={text => (
                                    <Form.Item>
                                        <Switch
                                            checked={values.options.merge.isActive}
                                            checkedChildren={t("other.active", {ns: "common"})}
                                            unCheckedChildren={t("other.passive", {ns: "common"})}
                                            data-testid="orderMergeableSwitch"
                                            onChange={e => {
                                                setFieldValue("options.merge.isActive", e);
                                                setIsCompanyFormChanged(true);
                                            }}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                            <Column
                                title={`${t("fields.mergeOptions.poolOrderMerge.label", {
                                    ns: "common"
                                })} (${t("fields.mergeOptions.poolOrderMerge.short", {
                                    ns: "common"
                                })})`}
                                dataIndex="isPoolMergeActive"
                                render={text => (
                                    <Form.Item>
                                        <Switch
                                            checked={values.options.merge.isPoolMergeActive}
                                            checkedChildren={t("other.active", {ns: "common"})}
                                            unCheckedChildren={t("other.passive", {ns: "common"})}
                                            data-testid="orderMergeableSwitch"
                                            onChange={e => {
                                                setFieldValue("options.merge.isPoolMergeActive", e);
                                                setIsCompanyFormChanged(true);
                                            }}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                            <Column
                                title={`${t("fields.mergeOptions.clickToDoorTime.label", {
                                    ns: "common"
                                })}(${t("units.minute.short", {
                                    ns: "common"
                                })}) (${t("fields.mergeOptions.clickToDoorTime.short", {
                                    ns: "common"
                                })})`}
                                dataIndex="clickToDoorTime"
                                render={text => (
                                    <Form.Item
                                        validateStatus={
                                            getIn(errors, `options.merge.clickToDoorTime`) &&
                                            getIn(touched, `options.merge.clickToDoorTime`)
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            getIn(errors, `options.merge.clickToDoorTime`) &&
                                            getIn(touched, `options.merge.clickToDoorTime`)
                                                ? getIn(errors, `options.merge.clickToDoorTime`)
                                                : null
                                        }
                                    >
                                        <Input
                                            type="number"
                                            name="clickToDoorTime"
                                            data-testid="clickToDoorTime"
                                            value={
                                                values.options.merge.isActive
                                                    ? values.options.merge.clickToDoorTime
                                                    : companyData?.data?.options?.merge
                                                          ?.clickToDoorTime
                                            }
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.merge.clickToDoorTime",
                                                    e.target.value || ""
                                                );
                                            }}
                                            disabled={!values.options.merge.isActive}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                            <Column
                                title={`${t("fields.mergeOptions.prepTime.label", {
                                    ns: "common"
                                })}(${t("units.minute.short", {
                                    ns: "common"
                                })}) (${t("fields.mergeOptions.prepTime.short", {ns: "common"})})`}
                                dataIndex="prepTime"
                                render={text => (
                                    <Form.Item
                                        validateStatus={
                                            getIn(errors, `options.merge.prepTime`) &&
                                            getIn(touched, `options.merge.prepTime`)
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            getIn(errors, `options.merge.prepTime`) &&
                                            getIn(touched, `options.merge.prepTime`)
                                                ? getIn(errors, `options.merge.prepTime`)
                                                : null
                                        }
                                    >
                                        <Input
                                            type="number"
                                            name="prepTime"
                                            data-testid="prepTime"
                                            value={
                                                values.options.merge.isActive
                                                    ? values.options.merge.prepTime
                                                    : companyData?.data?.options?.merge?.prepTime
                                            }
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.merge.prepTime",
                                                    e.target.value || ""
                                                );
                                            }}
                                            disabled={!values.options.merge.isActive}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                            <Column
                                title={`${t("fields.mergeOptions.prepTimeBufferPercent.label", {
                                    ns: "common"
                                })}(%) (${t("fields.mergeOptions.prepTimeBufferPercent.short", {
                                    ns: "common"
                                })})`}
                                dataIndex="prepTimeBufferPercent"
                                render={text => (
                                    <Form.Item
                                        validateStatus={
                                            getIn(errors, `options.merge.prepTimeBufferPercent`) &&
                                            getIn(touched, `options.merge.prepTimeBufferPercent`)
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            getIn(errors, `options.merge.prepTimeBufferPercent`) &&
                                            getIn(touched, `options.merge.prepTimeBufferPercent`)
                                                ? getIn(
                                                      errors,
                                                      `options.merge.prepTimeBufferPercent`
                                                  )
                                                : null
                                        }
                                    >
                                        <Input
                                            type="number"
                                            name="prepTimeBufferPercent"
                                            data-testid="prepTimeBufferPercent"
                                            value={
                                                values.options.merge.isActive
                                                    ? values.options.merge.prepTimeBufferPercent
                                                    : companyData?.data?.options?.merge
                                                          ?.prepTimeBufferPercent
                                            }
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.merge.prepTimeBufferPercent",
                                                    e.target.value || ""
                                                );
                                            }}
                                            disabled={!values.options.merge.isActive}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                            <Column
                                title={`${t("fields.mergeOptions.handOverTime.label", {
                                    ns: "common"
                                })}(${t("units.minute.short", {
                                    ns: "common"
                                })}) (${t("fields.mergeOptions.handOverTime.short", {
                                    ns: "common"
                                })})`}
                                dataIndex="handOverTime"
                                render={text => (
                                    <Form.Item
                                        validateStatus={
                                            getIn(errors, `options.merge.handOverTime`) &&
                                            getIn(touched, `options.merge.handOverTime`)
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            getIn(errors, `options.merge.handOverTime`) &&
                                            getIn(touched, `options.merge.handOverTime`)
                                                ? getIn(errors, `options.merge.handOverTime`)
                                                : null
                                        }
                                    >
                                        <Input
                                            type="number"
                                            name="handOverTime"
                                            data-testid="handOverTime"
                                            value={
                                                values.options.merge.isActive
                                                    ? values.options.merge.handOverTime
                                                    : companyData?.data?.options?.merge
                                                          ?.handOverTime
                                            }
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.merge.handOverTime",
                                                    e.target.value || ""
                                                );
                                            }}
                                            disabled={!values.options.merge.isActive}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                            <Column
                                title={`${t("fields.mergeOptions.mergeMaxOrder.label", {
                                    ns: "common"
                                })}(${t("units.piece.long", {
                                    ns: "common"
                                })}) (${t("fields.mergeOptions.mergeMaxOrder.short", {
                                    ns: "common"
                                })})`}
                                dataIndex="maxOrder"
                                render={text => (
                                    <Form.Item
                                        validateStatus={
                                            getIn(errors, `options.merge.maxOrder`) &&
                                            getIn(touched, `options.merge.maxOrder`)
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            getIn(errors, `options.merge.maxOrder`) &&
                                            getIn(touched, `options.merge.maxOrder`)
                                                ? getIn(errors, `options.merge.maxOrder`)
                                                : null
                                        }
                                    >
                                        <Input
                                            type="number"
                                            name="maxOrder"
                                            data-testid="maxOrder"
                                            value={
                                                values.options.merge.isActive
                                                    ? values.options.merge.maxOrder
                                                    : companyData?.data?.options?.merge?.maxOrder
                                            }
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.merge.maxOrder",
                                                    e.target.value || ""
                                                );
                                            }}
                                            disabled={!values.options.merge.isActive}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                            <Column
                                title={`${t(
                                    "fields.mergeOptions.mergeWithOtherVehicleTypes.label",
                                    {ns: "common"}
                                )} (${t("fields.mergeOptions.mergeWithOtherVehicleTypes.short", {
                                    ns: "common"
                                })})`}
                                dataIndex="mergeWithOtherTypes"
                                render={text => (
                                    <Form.Item>
                                        <Switch
                                            checked={values.options.merge.mergeWithOtherTypes}
                                            checkedChildren={t("other.active", {ns: "common"})}
                                            unCheckedChildren={t("other.passive", {ns: "common"})}
                                            data-testid="mergeWithOtherTypes"
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.merge.mergeWithOtherTypes",
                                                    e
                                                );
                                                setIsCompanyFormChanged(true);
                                            }}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                            <Column
                                title={`${t("fields.mergeOptions.mergeWithOtherCompanies.label2", {
                                    ns: "common"
                                })} (${t("fields.mergeOptions.mergeWithOtherCompanies.short", {
                                    ns: "common"
                                })})`}
                                dataIndex="mergeWithOtherCompanies"
                                render={text => (
                                    <Form.Item>
                                        <Switch
                                            checked={values.options.merge.mergeWithOtherCompanies}
                                            checkedChildren={t("other.active", {ns: "common"})}
                                            unCheckedChildren={t("other.passive", {ns: "common"})}
                                            data-testid="mergeWithOtherCompanies"
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.merge.mergeWithOtherCompanies",
                                                    e
                                                );
                                                setIsCompanyFormChanged(true);
                                            }}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                            <Column
                                title={`${t("fields.mergeOptions.maxPickup.label", {
                                    ns: "common"
                                })}(${t("units.piece.long", {
                                    ns: "common"
                                })}) (${t("fields.mergeOptions.maxPickup.short", {ns: "common"})})`}
                                dataIndex="maxPickup"
                                render={text => (
                                    <Form.Item
                                        validateStatus={
                                            getIn(errors, `options.merge.maxPickup`) &&
                                            getIn(touched, `options.merge.maxPickup`)
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            getIn(errors, `options.merge.maxPickup`) &&
                                            getIn(touched, `options.merge.maxPickup`)
                                                ? getIn(errors, `options.merge.maxPickup`)
                                                : null
                                        }
                                    >
                                        <Input
                                            type="number"
                                            name="maxPickup"
                                            data-testid="maxPickup"
                                            value={
                                                values.options.merge.isActive
                                                    ? values.options.merge.maxPickup
                                                    : companyData?.data?.options?.merge?.maxPickup
                                            }
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.merge.maxPickup",
                                                    e.target.value || ""
                                                );
                                            }}
                                            disabled={!values.options.merge.isActive}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                            <Column
                                title={`${t("fields.mergeOptions.regionRestriction.label", {
                                    ns: "common"
                                })} (${t("fields.mergeOptions.regionRestriction.short", {
                                    ns: "common"
                                })})`}
                                dataIndex="regionRestriction"
                                render={text => (
                                    <Form.Item>
                                        <Switch
                                            checked={values.options.merge.regionRestriction}
                                            checkedChildren={t("other.active", {ns: "common"})}
                                            unCheckedChildren={t("other.passive", {ns: "common"})}
                                            data-testid="regionRestriction"
                                            onChange={e => {
                                                setFieldValue("options.merge.regionRestriction", e);
                                                setIsCompanyFormChanged(true);
                                            }}
                                        />
                                    </Form.Item>
                                )}
                                align="center"
                            />
                        </FormikProvider>
                    </Table>
                    <CompanyButtonContainer justify="end" align="middle">
                        <Checkbox
                            checked={values?.applyMergeOptions}
                            data-testid="applyToAllSubCompaniesCheckbox"
                            onChange={e => setFieldValue("applyMergeOptions", e.target.checked)}
                        >
                            {handleCheckBoxText(upperGroup, regionGroupsNames, searchText, city)}
                        </Checkbox>
                        <Button
                            htmlType="submit"
                            disabled={!isCompanyFormChanged || isSubmitting}
                            type="primary"
                            data-testid="submitButton"
                        >
                            {t("actions.save", {ns: "common"})}
                        </Button>
                    </CompanyButtonContainer>
                </Form>
            </CompanyContanier>
            <Col span={12} data-testid="subCompaniesFilterCard">
                <FilterContainer type="flex" gutter={[20, 20]}>
                    <Col span={6}>
                        <CityFilter
                            value={city}
                            onChange={value => {
                                setCurrentPage(1);
                                setCity(value);
                                setRegionGroups([]);
                            }}
                        />
                    </Col>
                    <Col span={6}>
                        <h5>{t("fields.upperGroup", {ns: "common"})}</h5>
                        <SelectField
                            value={upperGroup}
                            onChange={value => {
                                setCurrentPage(1);
                                setUpperGroup(value);
                            }}
                            disabled={!city}
                            notFoundContent={t("filters.dataNotFound", {ns: "common"})}
                            placeholder={t("filters.addFilter", {ns: "common"})}
                            optionFilterProp="children"
                            data-testid="upperGroupSelect"
                            filterOption={(input, option) =>
                                option.children
                                    .toString()
                                    .toLowerCase()
                                    .indexOf(input.toLowerCase()) >= 0
                            }
                            filterSort={(optionA, optionB) =>
                                optionA.children
                                    .toString()
                                    .toLowerCase()
                                    .localeCompare(optionB.children.toString().toLowerCase())
                            }
                        >
                            <SelectField.Option value="">
                                {t("filters.all", {ns: "common"})}
                            </SelectField.Option>
                            {upperGroups.map(upperGroup => (
                                <SelectField.Option
                                    key={upperGroup.group}
                                    value={upperGroup.group}
                                    data-testid="upperGroupSelect_option"
                                >
                                    {upperGroup.group}
                                </SelectField.Option>
                            ))}
                        </SelectField>
                    </Col>
                    <Col span={6}>
                        <h5>{t("fields.regionGroup", {ns: "common"})}</h5>
                        <SelectField
                            value={regionGroups}
                            onChange={(value, children) => {
                                setCurrentPage(1);
                                setRegionGroups(value);
                                setRegionGroupsNames(children);
                            }}
                            disabled={!city || upperGroup === ""}
                            notFoundContent={t("filters.dataNotFound", {ns: "common"})}
                            mode="multiple"
                            placeholder={t("filters.addFilter", {ns: "common"})}
                            optionFilterProp="children"
                            data-testid="regionGroupSelect"
                            filterOption={(input, option) =>
                                option.children
                                    .toString()
                                    .toLowerCase()
                                    .indexOf(input.toLowerCase()) >= 0
                            }
                            filterSort={(optionA, optionB) =>
                                optionA.children
                                    .toString()
                                    .toLowerCase()
                                    .localeCompare(optionB.children.toString().toLowerCase())
                            }
                        >
                            {upperGroups
                                .find(c => c.group == upperGroup)
                                ?.regiongroups.map(regionGroupItem => (
                                    <SelectField.Option
                                        key={regionGroupItem._id}
                                        value={regionGroupItem._id}
                                        data-testid="regionGroupSelect_option"
                                    >
                                        {regionGroupItem.name}
                                    </SelectField.Option>
                                ))}
                        </SelectField>
                    </Col>
                    <Col span={6}>
                        <h5>{t("filters.search", {ns: "common"})}</h5>
                        <Input.Search
                            value={text}
                            data-testid="searchInput"
                            onSearch={value => {
                                setCurrentPage(1);
                                setSearchText(value);
                            }}
                            onChange={event => {
                                setText(event.target.value);
                            }}
                        ></Input.Search>
                    </Col>
                </FilterContainer>
            </Col>
            <SubCompanyContainer>
                <Table
                    rowKey="_id"
                    data-testid="subCompaniesTable"
                    dataSource={subCompanyData?.rows}
                    loading={subCompanyLoading}
                    pagination={{
                        total: subCompanyData.dataCount,
                        current: currentPage,
                        onChange: async page => {
                            setCurrentPage(page);
                            history.push("/companies/merge-options/" + companyId + "?page=" + page);
                        },
                        hideOnSinglePage: true,
                        showSizeChanger: false
                    }}
                    locale={{
                        emptyText: <EmptyMessage />
                    }}
                >
                    <Column
                        dataIndex="name"
                        title={t("fields.subcompany", {ns: "common"})}
                        render={text => <span data-testid="name">{text}</span>}
                    />
                    <Column
                        dataIndex="city"
                        title={t("fields.city", {ns: "common"})}
                        render={text => <span data-testid="city">{text}</span>}
                    />
                    <Column
                        dataIndex={"upperRegionGroup"}
                        title={t("fields.upperGroup", {ns: "common"})}
                        render={text => <span data-testid="upperRegionGroup">{text}</span>}
                    />
                    <Column
                        dataIndex={"regionGroup"}
                        title={t("fields.regionGroup", {ns: "common"})}
                        render={text => <span data-testid="regionGroup">{text?.name}</span>}
                    />

                    <Column
                        dataIndex="isActive"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("fields.mergeOptions.orderMerge.short", {ns: "common"})}
                        render={(text, record, index) => {
                            return (
                                <Switch
                                    data-testid="orderMergeableSwitch"
                                    checked={subCompanyData.rows[index]?.options?.merge?.isActive}
                                    onChange={e => {
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.merge.isActive = e;
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="isPoolMergeActive"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("fields.mergeOptions.poolOrderMerge.short", {ns: "common"})}
                        render={(text, record, index) => {
                            return (
                                <Switch
                                    data-testid="orderMergeableSwitch"
                                    checked={
                                        subCompanyData.rows[index]?.options?.merge
                                            ?.isPoolMergeActive
                                    }
                                    onChange={e => {
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.merge.isPoolMergeActive = e;
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="clickToDoorTime"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("fields.mergeOptions.clickToDoorTime.short", {ns: "common"})}
                        render={(text, record, index) => {
                            return (
                                <Input
                                    disabled={!subCompanyData.rows[index]?.options?.merge?.isActive}
                                    type="number"
                                    name="clickToDoorTime"
                                    data-testid="clickToDoorTime"
                                    value={
                                        subCompanyData.rows[index]?.options?.merge?.clickToDoorTime
                                    }
                                    onChange={e => {
                                        e.persist();
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.merge.clickToDoorTime =
                                                e.target?.value || "";
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="prepTime"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("fields.mergeOptions.prepTime.short", {ns: "common"})}
                        render={(text, record, index) => {
                            return (
                                <Input
                                    type="number"
                                    name="prepTime"
                                    data-testid="prepTime"
                                    disabled={!subCompanyData.rows[index]?.options?.merge?.isActive}
                                    value={subCompanyData.rows[index]?.options?.merge?.prepTime}
                                    onChange={e => {
                                        e.persist();
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.merge.prepTime = e.target?.value || "";
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="prepTimeBufferPercent"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("fields.mergeOptions.prepTimeBufferPercent.short", {ns: "common"})}
                        render={(text, record, index) => {
                            return (
                                <Input
                                    type="number"
                                    name="prepTimeBufferPercent"
                                    data-testid="prepTimeBufferPercent"
                                    disabled={!subCompanyData.rows[index]?.options?.merge?.isActive}
                                    value={
                                        subCompanyData.rows[index]?.options?.merge
                                            ?.prepTimeBufferPercent
                                    }
                                    onChange={e => {
                                        e.persist();
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.merge.prepTimeBufferPercent =
                                                e.target?.value || "";
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="handOverTime"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("fields.mergeOptions.handOverTime.short", {ns: "common"})}
                        render={(text, record, index) => {
                            return (
                                <Input
                                    type="number"
                                    name="handOverTime"
                                    data-testid="handOverTime"
                                    disabled={!subCompanyData.rows[index]?.options?.merge?.isActive}
                                    value={subCompanyData.rows[index]?.options?.merge?.handOverTime}
                                    onChange={e => {
                                        e.persist();
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.merge.handOverTime =
                                                e.target?.value || "";
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="maxOrder"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("fields.mergeOptions.mergeMaxOrder.short", {ns: "common"})}
                        render={(text, record, index) => {
                            return (
                                <Input
                                    type="number"
                                    name="maxOrder"
                                    data-testid="maxOrder"
                                    disabled={!subCompanyData.rows[index]?.options?.merge?.isActive}
                                    value={subCompanyData.rows[index]?.options?.merge?.maxOrder}
                                    onChange={e => {
                                        e.persist();
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.merge.maxOrder = e.target?.value || "";
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="mergeWithOtherTypes"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("fields.mergeOptions.mergeWithOtherVehicleTypes.short", {
                            ns: "common"
                        })}
                        render={(text, record, index) => {
                            return (
                                <Switch
                                    data-testid="mergeWithOtherTypes"
                                    checked={
                                        subCompanyData.rows[index]?.options?.merge
                                            ?.mergeWithOtherTypes
                                    }
                                    onChange={e => {
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.merge.mergeWithOtherTypes = e;
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="mergeWithOtherCompanies"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("fields.mergeOptions.mergeWithOtherCompanies.short", {
                            ns: "common"
                        })}
                        render={(text, record, index) => {
                            return (
                                <Switch
                                    data-testid="mergeWithOtherCompanies"
                                    checked={
                                        subCompanyData.rows[index]?.options?.merge
                                            ?.mergeWithOtherCompanies
                                    }
                                    onChange={e => {
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.merge.mergeWithOtherCompanies = e;
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="maxPickup"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("fields.mergeOptions.maxPickup.short", {ns: "common"})}
                        render={(text, record, index) => {
                            return (
                                <Input
                                    type="number"
                                    name="maxPickup"
                                    data-testid="maxPickup"
                                    disabled={!subCompanyData.rows[index]?.options?.merge?.isActive}
                                    value={subCompanyData.rows[index]?.options?.merge?.maxPickup}
                                    onChange={e => {
                                        e.persist();
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.merge.maxPickup = e.target?.value || "";
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="regionRestriction"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("fields.mergeOptions.regionRestriction.short", {ns: "common"})}
                        render={(text, record, index) => {
                            return (
                                <Switch
                                    data-testid="regionRestriction"
                                    checked={
                                        subCompanyData.rows[index]?.options?.merge
                                            ?.regionRestriction
                                    }
                                    onChange={e => {
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.merge.regionRestriction = e;
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="_id"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        render={(text, record, index) =>
                            showSubmitButton[text] && (
                                <Button
                                    disabled={isSubCompanySubmitting}
                                    onClick={() => onSubCompanySubmit(index)}
                                    type="primary"
                                    data-testid="submitButton"
                                >
                                    {t("actions.save", {ns: "common"})}
                                </Button>
                            )
                        }
                    />
                </Table>
            </SubCompanyContainer>
        </div>
    );
};
